import React, { useEffect, useState, useContext, useMemo } from "react";
import '../css/chat.css'
import userProfile1 from '../../../assets/images/userProfile1.png'
import userProfile2 from '../../../assets/images/userProfile2.png'
import userProfile3 from '../../../assets/images/userProfile3.png'
import userProfile4 from '../../../assets/images/userProfile4.png'
import userProfile5 from '../../../assets/images/userProfile5.png'
import userProfile6 from '../../../assets/images/userProfile6.png'
import { getRequest } from '../../apiRequests/requestApi';
import { UserContext } from '../../../utils/contexts/UserContext';
import CustomSvg from "../../svgs/CustomSvg";
import { FaSearch, FaSpinner, FaUserFriends } from 'react-icons/fa';

export default function ChatUsers({ selectActiveChat, activeChat, closeUsersOffcanvas }){
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [userStatus, setUserStatus] = useState({});
    const userContext = useContext(UserContext);
    const currentUserId = userContext?.user?._id;

    // Fetch users
    useEffect(() => {
        async function fetchUsers() {
            setLoading(true);
            setError("");
            try {
                const response = await getRequest("/gamers");
                if (response.success) {
                    // Filter out the current user from the list
                    const filteredUsers = response.data.filter(user => user._id !== currentUserId);
                    setUsers(filteredUsers);
                    
                    // Initialize random online status for demo purposes
                    // In a real app, this would come from the server
                    const statusObj = {};
                    filteredUsers.forEach(user => {
                        statusObj[user._id] = Math.random() > 0.5;
                    });
                    setUserStatus(statusObj);
                } else {
                    setError(response.message || "Failed to fetch users");
                }
            } catch (err) {
                setError(err.message || "Failed to fetch users");
            }
            setLoading(false);
        }
        fetchUsers();
        
        // Refresh user list every 60 seconds
        const interval = setInterval(fetchUsers, 60000);
        return () => clearInterval(interval);
    }, [currentUserId]);

    // Filter users based on search query
    const filteredUsers = useMemo(() => {
        if (!searchQuery.trim()) return users;
        
        return users.filter(user => {
            const name = (user.name || user.username || "").toLowerCase();
            return name.includes(searchQuery.toLowerCase());
        });
    }, [users, searchQuery]);
    
    // Handle search input change
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };
    
    // Clear search
    const clearSearch = () => {
        setSearchQuery("");
    };

    // Render user list
    const renderUsers = () => {
        if (loading) {
            return (
                <div className="chat-users-loading text-center py-5">
                    <FaSpinner className="fa-spin mb-3" size={24} color="#BD3193" />
                    <p className="small-txt txt-FFF opacity-_7">Loading contacts...</p>
                </div>
            );
        }
        
        if (error) {
            return (
                <div className="chat-users-error text-center py-4">
                    <p className="txt-FFF opacity-_7 mb-2">{error}</p>
                    <button 
                        className="btn btn-sm btn-outline-light mt-2"
                        onClick={() => window.location.reload()}
                    >
                        Retry
                    </button>
                </div>
            );
        }
        
        if (filteredUsers.length === 0) {
            return (
                <div className="chat-users-empty text-center py-4">
                    <p className="txt-FFF opacity-_7">
                        {searchQuery ? "No users match your search" : "No contacts found"}
                    </p>
                    {searchQuery && (
                        <button 
                            className="btn btn-sm btn-outline-light mt-2"
                            onClick={clearSearch}
                        >
                            Clear Search
                        </button>
                    )}
                </div>
            );
        }
        
        return filteredUsers.map((user, i) => {
            // Use fallback profile images if needed
            const profile = user.avatar || userProfile1;
            const name = user.name || user.username || "Unknown";
            const isOnline = userStatus[user._id] || false;

            const onUserClick = () => {
                selectActiveChat(user);
                return closeUsersOffcanvas && closeUsersOffcanvas();
            }

            const isActive = activeChat && activeChat._id === user._id;

            return (
                <div
                    key={user._id || user.id || i}
                    onClick={onUserClick}
                    className={`${isActive ? 'chat-user-active' : ''} chat-chat-single-user-container p-3 d-flex align-items-center mb-3 clickable position-relative`}
                >
                    <div className="position-relative">
                        <div className=" border-2 border-warning rounded-circle d-flex flex-column align-items-center justify-content-center p-1">
                            <img 
                                src={profile}  
                                style={{width: '50px', height: '50px', objectFit: 'cover'}}
                                className='rounded-circle' 
                                alt={name} 
                            />
                        </div>
                        {isOnline && (
                            <div className="chat-user-online-indicator"></div>
                        )}
                    </div>

                    <div className="mx-3 flex-grow-1 text-white">
                        <p className="m-0 p-0 small-txt font-weight-600 txt-FFF font-family-poppins">{name}</p>
                        <p className="m-0 p-0 extra-small-txt opacity-_7 txt-FFF font-family-poppins">
                            {isOnline ? 'Online' : 'Offline'}
                        </p>
                    </div>
                    
                    {isActive && (
                        <div className="chat-user-active-indicator"></div>
                    )}
                </div>
            );
        });
    };

    return (
        <div className="chat-chat-users-container d-flex flex-column h-100">
            <div className="chat-users-header p-3">
                <h4 className="d-flex align-items-center text-white mb-3">
                    <FaUserFriends className="me-2" color="#BD3193" />
                    <span className="txt-FFF">Chats</span>
                    <span className="chat-users-count ms-2">({users.length})</span>
                </h4>
                
                <div className="chat-users-search position-relative">
                    <input
                        type="text"
                        className="chat-users-search-input text-white"
                        placeholder="Search users..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                    />
                    <div className="chat-users-search-icon">
                        <FaSearch size={14} color="#BD3193" />
                    </div>
                    {searchQuery && (
                        <button 
                            className="chat-users-search-clear"
                            onClick={clearSearch}
                        >
                            &times;
                        </button>
                    )}
                </div>
            </div>
            
            <div className="chat-users-list p-3 flex-grow-1 overflow-auto">
                {renderUsers()}
            </div>
        </div>
    );
}